import React, { useEffect, useState } from "react";
import { Cookie, X, ShieldCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { getConsent, hasDecision, acceptAll, rejectAll, setConsent } from "@/lib/consent";

const CATS = [
  {
    key: "necessary",
    title: "Necessari",
    locked: true,
    desc: "Indispensabili per il funzionamento del sito (navigazione, sicurezza, preferenze di consenso). Sempre attivi.",
  },
  {
    key: "preferences",
    title: "Preferenze",
    desc: "Ricordano le tue scelte (es. impostazioni) per offrirti un'esperienza più personalizzata.",
  },
  {
    key: "analytics",
    title: "Statistiche",
    desc: "Ci aiutano a capire come viene usato il sito, in forma aggregata, per migliorarlo.",
  },
  {
    key: "marketing",
    title: "Marketing",
    desc: "Utilizzati per mostrare contenuti e comunicazioni più pertinenti ai tuoi interessi.",
  },
];

const btnBase =
  "inline-flex items-center justify-center rounded-full font-bold text-sm px-5 py-2.5 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vs-green/40";

export function CookieConsent() {
  const [decided, setDecided] = useState(true); // avoid flash before mount check
  const [panelOpen, setPanelOpen] = useState(false);
  const [prefs, setPrefs] = useState(getConsent());

  useEffect(() => {
    setDecided(hasDecision());
    setPrefs(getConsent());
    const onOpen = () => {
      setPrefs(getConsent());
      setPanelOpen(true);
    };
    window.addEventListener("vs-open-cookie-preferences", onOpen);
    return () => window.removeEventListener("vs-open-cookie-preferences", onOpen);
  }, []);

  const bannerVisible = !decided && !panelOpen;

  const finish = () => {
    setDecided(true);
    setPanelOpen(false);
  };

  const onAcceptAll = () => { acceptAll(); finish(); };
  const onRejectAll = () => { rejectAll(); finish(); };
  const onSavePrefs = () => { setConsent(prefs); finish(); };
  const toggle = (key, val) => setPrefs((p) => ({ ...p, [key]: val }));

  return (
    <>
      {/* ---------- Consent banner ---------- */}
      {bannerVisible && (
        <div
          className="fixed inset-x-0 bottom-0 z-[140] px-4 pb-4 md:px-6 md:pb-6"
          role="region"
          aria-label="Preferenze cookie"
          data-testid="cookie-banner"
        >
          <div className="mx-auto max-w-4xl rounded-2xl bg-white shadow-vs-lift ring-1 ring-vs-text/10 p-5 md:p-6">
            <div className="flex items-start gap-3">
              <span className="hidden sm:flex w-11 h-11 shrink-0 rounded-xl bg-vs-mint items-center justify-center text-vs-green">
                <Cookie className="w-6 h-6" />
              </span>
              <div className="flex-1">
                <h2 className="font-serif text-xl md:text-2xl font-extrabold text-vs-text">Rispettiamo la tua privacy</h2>
                <p className="mt-2 text-vs-text/70 text-sm leading-relaxed">
                  Usiamo i cookie per far funzionare il sito e, con il tuo consenso, per statistiche e marketing.
                  I cookie di statistica e marketing restano disattivati finché non accetti. Puoi cambiare idea in qualsiasi momento.
                </p>
                <div className="mt-5 flex flex-col sm:flex-row flex-wrap gap-3">
                  <button
                    className={`${btnBase} bg-vs-green text-white hover:-translate-y-0.5 hover:shadow-vs-soft`}
                    onClick={onAcceptAll}
                    data-testid="cookie-accept-all"
                  >
                    Accetta tutti
                  </button>
                  <button
                    className={`${btnBase} bg-vs-bg text-vs-text hover:bg-vs-mint`}
                    onClick={onRejectAll}
                    data-testid="cookie-reject-all"
                  >
                    Rifiuta tutti
                  </button>
                  <button
                    className={`${btnBase} bg-white text-vs-green ring-1 ring-vs-green/30 hover:bg-vs-mint/60`}
                    onClick={() => { setPrefs(getConsent()); setPanelOpen(true); }}
                    data-testid="cookie-customize"
                  >
                    Personalizza
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Preferences panel ---------- */}
      {panelOpen && (
        <div className="fixed inset-0 z-[150] flex items-end md:items-center justify-center p-3 md:p-6" role="dialog" aria-modal="true" aria-label="Preferenze cookie" data-testid="cookie-preferences-dialog">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPanelOpen(false)} data-testid="cookie-panel-overlay" />
          <div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-vs-lift">
            <div className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-vs-text/10">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="w-5 h-5 text-vs-green" />
                <h2 className="font-serif text-lg md:text-xl font-extrabold text-vs-text">Preferenze cookie</h2>
              </div>
              <button className="p-1.5 rounded-full hover:bg-vs-bg text-vs-text/60" onClick={() => setPanelOpen(false)} aria-label="Chiudi" data-testid="cookie-panel-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 md:px-6 py-4 space-y-4">
              {CATS.map((c) => (
                <div key={c.key} className="flex items-start justify-between gap-4 rounded-xl bg-vs-bg/60 p-4">
                  <div>
                    <div className="font-bold text-vs-text text-sm">{c.title}</div>
                    <p className="text-vs-text/65 text-xs mt-1 leading-relaxed">{c.desc}</p>
                  </div>
                  {c.locked ? (
                    <span className="text-xs font-bold text-vs-green whitespace-nowrap mt-0.5" data-testid="cookie-toggle-necessary">Sempre attivi</span>
                  ) : (
                    <Switch
                      checked={!!prefs[c.key]}
                      onCheckedChange={(v) => toggle(c.key, v)}
                      className="data-[state=checked]:bg-vs-green mt-0.5"
                      data-testid={`cookie-toggle-${c.key}`}
                      aria-label={c.title}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="px-5 md:px-6 py-4 border-t border-vs-text/10 flex flex-col sm:flex-row gap-3">
              <button className={`${btnBase} bg-vs-green text-white flex-1`} onClick={onSavePrefs} data-testid="cookie-save-preferences">
                Salva preferenze
              </button>
              <button className={`${btnBase} bg-vs-bg text-vs-text flex-1`} onClick={onAcceptAll} data-testid="cookie-panel-accept-all">
                Accetta tutti
              </button>
              <button className={`${btnBase} bg-white text-vs-text ring-1 ring-vs-text/15 flex-1`} onClick={onRejectAll} data-testid="cookie-panel-reject-all">
                Rifiuta tutti
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Reopen preferences (always available after a decision) ---------- */}
      {decided && !panelOpen && (
        <button
          className="fixed bottom-4 left-4 z-[135] w-11 h-11 rounded-full bg-white shadow-vs-lift ring-1 ring-vs-text/10 flex items-center justify-center text-vs-green hover:bg-vs-mint hover:-translate-y-0.5 transition-all duration-300"
          onClick={() => { setPrefs(getConsent()); setPanelOpen(true); }}
          aria-label="Preferenze cookie"
          title="Preferenze cookie"
          data-testid="cookie-reopen-button"
        >
          <Cookie className="w-5 h-5" />
        </button>
      )}
    </>
  );
}
